/**
 * Shared leveled logger with categories and a runtime control surface (#577).
 *
 * Environment-agnostic (no deps, no imports): runs identically in Node and the
 * browser. Categories follow the existing `[Tag]` prefix convention
 * (`[ServerScheduler]` → `scheduler`, `[SyncAction]` → `sync`, ...).
 *
 * Usage:
 *   import { Log } from '../shared/Log.js';
 *   const log = Log.get('scheduler');
 *   log.info('tick complete', { tick: 42 });   // → INFO [scheduler] tick complete {"tick":42}
 *
 * Levels: error < warn < info < debug. Default level: info.
 * A category emits a record when the record's level is at or below the
 * category's effective level (per-category override, else default).
 *
 * Control surface:
 *   Server (env vars, read at module load):
 *     LOG_LEVEL=warn                  — default level for all categories
 *     LOG_DEBUG=sync,scheduler        — force listed categories to debug
 *   Client (read at module load, generalizing the ?debugWorld pattern):
 *     ?log=sync:debug,ui:warn         — URL param; a bare level ("?log=debug")
 *                                       sets the default level
 *     localStorage.isekaiLog          — same format, applied before the URL param
 *     window.isekaiLog.set('sync','debug') — runtime setter for automation agents
 *   Both:
 *     Log.setLevel('sync', 'debug')   — runtime setter (usable from tests)
 *     Log.setLevel('warn')            — one-arg form sets the default level
 *     Log.reset()                     — re-parse the control surface (tests)
 *
 * Output format (agentic-consumption friendly): one line per record,
 * `LEVEL [category] message key=value`. Object payloads are JSON-serialized
 * instead of being passed as extra console args (which render as
 * `[object Object]` to console-scraping automation).
 *
 * @module shared/Log
 */

/** Level names in severity order (index = numeric rank). */
export const LOG_LEVELS = Object.freeze(['error', 'warn', 'info', 'debug']);

const LEVEL_RANK = Object.freeze({ error: 0, warn: 1, info: 2, debug: 3 });

const DEFAULT_LEVEL = 'info';

/**
 * JSON-serialize a payload defensively (circular refs, BigInt, etc.).
 * Errors serialize to their stack (or message) so they stay greppable.
 * @param {*} value
 * @returns {string}
 */
function serialize(value) {
    // #578 / #577-audit: newline-escape user-controlled string args so a payload
    // containing "\n" can't forge additional (one-per-record) log lines. Error
    // stacks are intentionally multi-line and dev-controlled, so leave them intact.
    if (typeof value === 'string') { return value.replace(/\r\n|\r|\n/g, '\\n'); }
    if (value instanceof Error) { return value.stack || value.message; }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

/**
 * Per-category logger returned by {@link Log.get}. Method surface is a
 * console-compatible superset (`log` aliases `info`) so it can drop into the
 * existing `options.logger || console` injection seams unchanged.
 */
class CategoryLogger {
    /**
     * @param {LogController} controller - Owning controller (level lookups + sink)
     * @param {string} category - Category name, e.g. 'scheduler'
     */
    constructor(controller, category) {
        this._controller = controller;
        this.category = category;
    }

    /**
     * Whether a record at `level` would currently be emitted.
     * Lets hot paths skip building expensive payloads.
     * @param {string} level
     * @returns {boolean}
     */
    isEnabled(level) {
        const rank = LEVEL_RANK[level];
        if (rank === undefined) { return false; }
        return rank <= LEVEL_RANK[this._controller.getLevel(this.category)];
    }

    /**
     * @param {string} level
     * @param {Array<*>} args
     */
    _emit(level, args) {
        if (!this.isEnabled(level)) { return; }
        const line = `${level.toUpperCase()} [${this.category}] ${args.map(serialize).join(' ')}`;
        const sink = this._controller._sink;
        if (level === 'error') { sink.error(line); }
        else if (level === 'warn') { sink.warn(line); }
        else { sink.log(line); }
    }

    /** @param {...*} args */
    error(...args) { this._emit('error', args); }
    /** @param {...*} args */
    warn(...args) { this._emit('warn', args); }
    /** @param {...*} args */
    info(...args) { this._emit('info', args); }
    /** @param {...*} args */
    debug(...args) { this._emit('debug', args); }
    /**
     * Alias for {@link CategoryLogger#info} — console.log drop-in for the
     * `options.logger || console` seams.
     * @param {...*} args
     */
    log(...args) { this._emit('info', args); }
}

/**
 * Parse a control-surface spec string: comma-separated entries, each either
 * `category:level` or a bare `level` (sets the default). Unknown levels are
 * ignored (defensive: specs come from URL params / localStorage).
 * @param {string} spec - e.g. 'sync:debug,ui:warn' or 'debug'
 * @param {LogController} controller
 */
function applySpec(spec, controller) {
    if (!spec || typeof spec !== 'string') { return; }
    for (const entry of spec.split(',')) {
        const trimmed = entry.trim();
        if (!trimmed) { continue; }
        const sep = trimmed.indexOf(':');
        if (sep === -1) {
            const level = trimmed.toLowerCase();
            if (LEVEL_RANK[level] !== undefined) { controller.setLevel(level); }
        } else {
            const category = trimmed.slice(0, sep).trim();
            const level = trimmed.slice(sep + 1).trim().toLowerCase();
            if (category && LEVEL_RANK[level] !== undefined) {
                controller.setLevel(category, level);
            }
        }
    }
}

/** Singleton controller behind the exported {@link Log} facade. */
class LogController {
    constructor() {
        /** @type {Map<string, CategoryLogger>} */
        this._loggers = new Map();
        /** @type {Object} Console-shaped sink; injectable for tests */
        this._sink = console;
        this.reset();
    }

    /**
     * Re-initialize levels from the environment control surface.
     * Order (later wins): defaults → env vars → localStorage → URL param.
     * Safe to call from tests to restore a known state.
     */
    reset() {
        /** @type {string} */
        this._defaultLevel = DEFAULT_LEVEL;
        /** @type {Map<string, string>} */
        this._categoryLevels = new Map();

        // Server: env vars (guarded — absent in the browser).
        const env = globalThis.process?.env;
        if (env) {
            const envLevel = (env.LOG_LEVEL || '').toLowerCase();
            if (LEVEL_RANK[envLevel] !== undefined) { this._defaultLevel = envLevel; }
            if (env.LOG_DEBUG) {
                for (const category of env.LOG_DEBUG.split(',')) {
                    const trimmed = category.trim();
                    if (trimmed) { this._categoryLevels.set(trimmed, 'debug'); }
                }
            }
        }

        // Client: localStorage then URL param (guarded — generalizes the
        // ?debugWorld pattern in clientjs/GameMap.js).
        try {
            const stored = globalThis.localStorage?.getItem('isekaiLog');
            if (stored) { applySpec(stored, this); }
            const search = globalThis.location?.search;
            if (search) {
                applySpec(new URLSearchParams(search).get('log') || '', this);
            }
        } catch {
            // Storage/URL access can throw in locked-down contexts — keep defaults.
        }
    }

    /**
     * Get (or create) the logger for a category.
     * @param {string} category
     * @returns {CategoryLogger}
     */
    get(category) {
        let logger = this._loggers.get(category);
        if (!logger) {
            logger = new CategoryLogger(this, category);
            this._loggers.set(category, logger);
        }
        return logger;
    }

    /**
     * Set a level at runtime.
     * - `setLevel('sync', 'debug')` — override one category
     * - `setLevel('warn')` — set the default level
     * @param {string} categoryOrLevel
     * @param {string} [level]
     */
    setLevel(categoryOrLevel, level) {
        if (level === undefined) {
            const defaultLevel = String(categoryOrLevel).toLowerCase();
            if (LEVEL_RANK[defaultLevel] === undefined) {
                throw new Error(`[Log] Unknown level: ${categoryOrLevel}`);
            }
            this._defaultLevel = defaultLevel;
            return;
        }
        const normalized = String(level).toLowerCase();
        if (LEVEL_RANK[normalized] === undefined) {
            throw new Error(`[Log] Unknown level: ${level}`);
        }
        this._categoryLevels.set(categoryOrLevel, normalized);
    }

    /**
     * Effective level for a category (override, else default).
     * @param {string} category
     * @returns {string}
     */
    getLevel(category) {
        return this._categoryLevels.get(category) || this._defaultLevel;
    }

    /**
     * Replace the output sink (console-shaped: log/warn/error). Test seam.
     * @param {Object} sink
     */
    setSink(sink) {
        this._sink = sink || console;
    }
}

/** The shared logger singleton. */
export const Log = new LogController();

// Browser runtime control surface for automation agents mid-session:
//   window.isekaiLog.set('sync', 'debug')
// Guarded so the shared module stays inert under Node.
if (globalThis.window !== undefined) {
    globalThis.window.isekaiLog = {
        set: (categoryOrLevel, level) => Log.setLevel(categoryOrLevel, level),
        get: (category) => Log.getLevel(category),
        reset: () => Log.reset(),
    };
}

export default Log;
